import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../api";
import { fetchDefaultGrouping } from "../utils/storeSettings";

interface QueryInfo {
  text: string;
  weekPositions: (number | null)[];
  uniqueSearchUsers: number;
  orderCount: number;
  bestPosition: number | null;
}

interface QueryProduct {
  id: number | string;
  name: string;
  sku: string;
  groupName?: string | null;
  queries: QueryInfo[];
}

type Trend = "up" | "down" | "flat";
type GroupMode = "products" | "groups";

const formatDayTitles = () => {
  const start = new Date();
  start.setDate(start.getDate() - 6);
  return Array.from({ length: 7 }).map((_, idx) => {
    const d = new Date(start);
    d.setDate(start.getDate() + idx);
    return d
      .toLocaleDateString("ru-RU", { weekday: "short" })
      .replace(".", "")
      .replace(/\s/g, "");
  });
};

function getLastValue(values: (number | null)[]) {
  for (let i = values.length - 1; i >= 0; i -= 1) {
    const val = values[i];
    if (val !== null && val !== undefined) return val;
  }
  return null;
}

function getPrevValue(values: (number | null)[], beforeIndex: number) {
  for (let i = beforeIndex - 1; i >= 0; i -= 1) {
    const val = values[i];
    if (val !== null && val !== undefined) return val;
  }
  return null;
}

function getTrendData(weekPositions: (number | null)[]) {
  const lastIndex = (() => {
    for (let i = weekPositions.length - 1; i >= 0; i -= 1) {
      if (weekPositions[i] !== null && weekPositions[i] !== undefined) return i;
    }
    return -1;
  })();

  const yesterday = lastIndex >= 0 ? weekPositions[lastIndex] : null;
  const prev = lastIndex > 0 ? getPrevValue(weekPositions, lastIndex) : null;

  let type: Trend = "flat";
  let diff = 0;
  if (yesterday !== null && prev !== null) {
    diff = prev - yesterday;
    if (diff > 0) type = "up";
    else if (diff < 0) type = "down";
  }

  return { type, diff, yesterday };
}

const mobileQuery = "(max-width: 900px)";

const QueryStats: React.FC = () => {
  const [products, setProducts] = useState<QueryProduct[]>([]);
  const [selectedId, setSelectedId] = useState<number | string | null>(null);
  const [isSheetOpen, setSheetOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
      () =>
        typeof window !== "undefined" && window.matchMedia(mobileQuery).matches,
  );
  const [groupMode, setGroupMode] = useState<GroupMode>("products");
  const userChangedGroup = useRef(false);
  const [viewMode, setViewMode] = useState<"unique" | "orders" | "position">(
    "position",
  );

  const dayTitles = useMemo(() => formatDayTitles(), []);

  const handleGroupModeChange = (mode: GroupMode) => {
    userChangedGroup.current = true;
    setGroupMode(mode);
  };

  useEffect(() => {
    let isActive = true;
    fetchDefaultGrouping().then(mode => {
      if (!isActive || !mode || userChangedGroup.current) return;
      setGroupMode(mode);
    });
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    async function load() {
      const res = await apiFetch("/api/dashboard/query-stats");
      if (!res.ok) return;
      const data: QueryProduct[] = await res.json();
      setProducts(data);
      if (data.length) {
        setSelectedId(data[0].id);
      }
    }
    load().catch(console.error);
  }, []);

  useEffect(() => {
    const handler = () => {
      const mobile = window.matchMedia(mobileQuery).matches;
      setIsMobile(mobile);
      if (!mobile) {
        setSheetOpen(false);
        document.body.style.overflow = "";
      }
    };
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("resize", handler);
      document.body.style.overflow = "";
    };
  }, []);

  const selected = useMemo(
    () => products.find((p) => p.id === selectedId) || products[0],
    [products, selectedId],
  );

  const getTopQueries = useMemo(() => {
    const sorters = {
      unique: (a: QueryInfo, b: QueryInfo) =>
        b.uniqueSearchUsers - a.uniqueSearchUsers,
      orders: (a: QueryInfo, b: QueryInfo) => b.orderCount - a.orderCount,
      position: (a: QueryInfo, b: QueryInfo) => {
        const aPos = a.bestPosition ?? Number.POSITIVE_INFINITY;
        const bPos = b.bestPosition ?? Number.POSITIVE_INFINITY;
        return aPos - bPos;
      },
    } as const;

    return (queries: QueryInfo[]) => {
      const sorted = [...queries].sort(sorters[viewMode]);
      return sorted.slice(0, 3);
    };
  }, [viewMode]);

  const bestPosition = (positions: (number | null)[]) => {
    const nums = positions.filter(
      (p): p is number => p !== null && p !== undefined,
    );
    return nums.length ? Math.min(...nums) : null;
  };

  const groupedProducts = useMemo(() => {
    const groups = new Map<string, QueryProduct[]>();
    products.forEach((product) => {
      const groupName = product.groupName?.trim() || "Без категории";
      const list = groups.get(groupName) || [];
      list.push(product);
      groups.set(groupName, list);
    });
    return Array.from(groups.entries())
      .map(([groupName, items]) => ({ groupName, items }))
      .sort((a, b) =>
        a.groupName.localeCompare(b.groupName, "ru", { sensitivity: "base" }),
      );
  }, [products]);

  const handleSelect = (id: number | string) => {
    setSelectedId(id);
    if (isMobile) {
      setSheetOpen(true);
      document.body.style.overflow = "hidden";
    }
  };

  const closeSheet = () => {
    setSheetOpen(false);
    document.body.style.overflow = "";
  };

  if (!products.length) return null;

  return (
    <div className="max-w-7xl mx-auto mt-8 w-full bg-white text-gray-800 rounded-2xl p-4 sm:p-6 shadow">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">Поисковые позиции товаров</h2>
          <p className="text-sm text-gray-500">Данные за последние 7 дней</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 text-xs shadow-sm">
            <button
              type="button"
              className={`px-3 py-1 text-sm rounded-lg transition ${
                groupMode === "products"
                  ? "bg-indigo-600 text-white shadow"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
              onClick={() => handleGroupModeChange("products")}
            >
              по товарам
            </button>
            <button
              type="button"
              className={`px-3 py-1 text-sm rounded-lg transition ${
                groupMode === "groups"
                  ? "bg-indigo-600 text-white shadow"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
              onClick={() => handleGroupModeChange("groups")}
            >
              по категориям
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 bg-slate-100 border border-slate-200 rounded-xl p-1 text-xs">
            {[
              { key: "unique", label: "Уникальные поиски" },
              { key: "orders", label: "Заказы" },
              { key: "position", label: "Позиции" },
            ].map((mode) => (
              <button
                key={mode.key}
                type="button"
                onClick={() => setViewMode(mode.key as typeof viewMode)}
                className={`px-3 py-1 rounded-lg transition text-sm ${
                  viewMode === mode.key
                    ? "bg-indigo-600 text-white shadow"
                    : "text-slate-600 hover:bg-white"
                }`}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)]">
        <div className="lg:max-h-[620px] lg:overflow-y-auto lg:pr-1">
          {groupMode === "products" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {products.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => handleSelect(product.id)}
                  className="text-left bg-white border border-gray-200 hover:border-indigo-300 hover:bg-slate-50 hover:-translate-y-0.5 transition-all rounded-xl p-4 shadow-sm"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold leading-tight text-gray-900 truncate">
                        {product.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        Артикул товара: {product.sku}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {getTopQueries(product.queries).map((q) => {
                      const { type, diff, yesterday } = getTrendData(
                        q.weekPositions,
                      );
                      const badgeClass =
                        type === "up"
                          ? "bg-emerald-100 text-emerald-700"
                          : type === "down"
                            ? "bg-rose-100 text-rose-700"
                            : "bg-slate-100 text-slate-700";
                      const label =
                        type === "up"
                          ? `улучшение на ${diff}`
                          : type === "down"
                            ? `просадка на ${Math.abs(diff)}`
                            : "без изменений";
                      const current = yesterday ?? getLastValue(q.weekPositions);
                      return (
                        <div
                          key={q.text}
                          className="flex items-center justify-between gap-2"
                        >
                          <div
                            className="text-sm text-gray-800 truncate"
                            title={q.text}
                          >
                            {q.text}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs px-2 py-1 rounded-full bg-white border border-gray-300 text-gray-800">
                              {current ?? "—"}
                            </span>
                            <span
                              className={`text-[11px] px-2 py-1 rounded-full inline-flex items-center gap-1 ${badgeClass}`}
                            >
                              {type === "up" ? "▲" : type === "down" ? "▼" : "•"}
                              <span>{label}</span>
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {groupedProducts.map((group) => (
                <div key={group.groupName} className="space-y-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {group.groupName}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                    {group.items.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => handleSelect(product.id)}
                        className="text-left bg-white border border-gray-200 hover:border-indigo-300 hover:bg-slate-50 hover:-translate-y-0.5 transition-all rounded-xl p-4 shadow-sm"
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold leading-tight text-gray-900 truncate">
                              {product.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              Артикул товара: {product.sku}
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {getTopQueries(product.queries).map((q) => {
                            const { type, diff, yesterday } = getTrendData(
                              q.weekPositions,
                            );
                            const badgeClass =
                              type === "up"
                                ? "bg-emerald-100 text-emerald-700"
                                : type === "down"
                                  ? "bg-rose-100 text-rose-700"
                                  : "bg-slate-100 text-slate-700";
                            const label =
                              type === "up"
                                ? `улучшение на ${diff}`
                                : type === "down"
                                  ? `просадка на ${Math.abs(diff)}`
                                  : "без изменений";
                            const current =
                              yesterday ?? getLastValue(q.weekPositions);
                            return (
                              <div
                                key={q.text}
                                className="flex items-center justify-between gap-2"
                              >
                                <div
                                  className="text-sm text-gray-800 truncate"
                                  title={q.text}
                                >
                                  {q.text}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-xs px-2 py-1 rounded-full bg-white border border-gray-300 text-gray-800">
                                    {current ?? "—"}
                                  </span>
                                  <span
                                    className={`text-[11px] px-2 py-1 rounded-full inline-flex items-center gap-1 ${badgeClass}`}
                                  >
                                    {type === "up"
                                      ? "▲"
                                      : type === "down"
                                        ? "▼"
                                        : "•"}
                                    <span>{label}</span>
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="relative">
          {isMobile && (
            <button
              type="button"
              aria-label="Закрыть детали"
              onClick={closeSheet}
              className={`fixed inset-0 bg-black/60 transition-opacity duration-200 ${
                isSheetOpen ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
            />
          )}

          <div
            className={`bg-gray-50 border border-gray-200 rounded-xl p-4 transition-transform duration-200 lg:static lg:translate-y-0 ${
              isMobile
                ? "fixed left-0 right-0 bottom-0 z-50 max-h-[75vh] rounded-t-2xl shadow-[0_-16px_48px_rgba(0,0,0,0.75)] pt-3"
                : ""
            } ${
              isMobile && selected
                ? isSheetOpen
                  ? "translate-y-0"
                  : "translate-y-full"
                : ""
            }`}
          >
            {isMobile && (
              <>
                <div className="w-10 h-1 rounded-full bg-gray-300 mx-auto mb-2" />
                <button
                  type="button"
                  onClick={closeSheet}
                  className="absolute right-4 top-3 text-gray-500 text-xl"
                >
                  ×
                </button>
              </>
            )}

            {!selected ? (
              <div className="text-center text-gray-500 text-sm">
                Выберите товар слева.
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    <div className="text-lg font-semibold text-gray-900 leading-tight">
                      {selected.name}
                    </div>
                    <div className="text-sm text-indigo-600">
                      Артикул товара: {selected.sku}
                    </div>
                    {selected.groupName && (
                      <div className="text-xs text-gray-500 mt-1">
                        Категория: {selected.groupName}
                      </div>
                    )}
                  </div>
                  {!isMobile && (
                    <div className="text-xs px-3 py-1 rounded-full border border-indigo-200 text-indigo-700 bg-indigo-50">
                      7 дней
                    </div>
                  )}
                </div>

                <div
                  className={`space-y-4 pr-1 ${
                    isMobile
                      ? "max-h-[58vh] overflow-y-auto"
                      : "lg:max-h-[520px] lg:overflow-y-auto"
                  }`}
                >
                  {getTopQueries(selected.queries).map((query) => {
                    const { type, diff, yesterday } = getTrendData(
                      query.weekPositions,
                    );
                    const best = bestPosition(query.weekPositions);
                    const badgeClass =
                      type === "up"
                        ? "bg-emerald-100 text-emerald-700"
                        : type === "down"
                          ? "bg-rose-100 text-rose-700"
                          : "bg-slate-100 text-slate-700";
                    const label =
                      type === "up"
                        ? `улучшение на ${diff}`
                        : type === "down"
                          ? `просадка на ${Math.abs(diff)}`
                          : "без изменений";
                    return (
                      <div
                        key={query.text}
                        className="border-t border-gray-200 pt-3 first:border-t-0 first:pt-0"
                      >
                        <div className="text-base font-medium text-gray-900">
                          {query.text}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-2">
                          <span>Вчера: {yesterday ?? "—"}</span>
                          <span
                            className={`px-2 py-1 rounded-full inline-flex items-center gap-1 ${badgeClass}`}
                          >
                            {type === "up" ? "▲" : type === "down" ? "▼" : "•"}{" "}
                            {label}
                          </span>
                        </div>
                        <div className="grid grid-cols-7 gap-2 text-xs text-gray-800">
                          {query.weekPositions.map((pos, idx) => (
                            <div
                              key={`${query.text}-${idx}`}
                              className={`rounded-md border px-2 py-2 text-center font-mono ${
                                pos !== null && best !== null && pos === best
                                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                  : "border-gray-200 bg-white"
                              }`}
                            >
                              <div className="text-[11px] text-gray-500">
                                {dayTitles[idx]}
                              </div>
                              <div className="text-sm">{pos ?? "—"}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  <div className="text-[11px] text-gray-500 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" />
                    Лучшая позиция за неделю
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueryStats;
